import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import { format, parseISO } from "date-fns";
import useStatusStore from "../../store/statusStore";
import { getClosureText, getReservationTimes, isReservationDayClosed } from "../../config/hours";
import {
    FormError,
    FormField,
    SentMessage,
    SpamProtection,
    turnstileRequired,
} from "../site/FormBits";

const today = () => format(new Date(), "yyyy-MM-dd");

const schema = yup.object().shape({
    vorname: yup.string().trim().required("Vorname ist erforderlich."),
    nachname: yup.string().trim().required("Nachname ist erforderlich."),
    telefon: yup
        .string()
        .matches(/^\+?[\d\s-]+$/, "Bitte geben Sie eine gültige Telefonnummer ein.")
        .required("Telefonnummer ist erforderlich."),
    email: yup
        .string()
        .email("Bitte geben Sie eine gültige E-Mail-Adresse ein.")
        .required("E-Mail ist erforderlich."),
    datum: yup
        .string()
        .required("Datum ist erforderlich.")
        .test("zukunft", "Bitte wählen Sie ein Datum ab heute.", (value) => !value || value >= today())
        .test(
            "geoeffnet",
            "An diesem Tag haben wir geschlossen.",
            (value) =>
                !value || !isReservationDayClosed(parseISO(value), useStatusStore.getState().closure),
        ),
    zeit: yup.string().required("Uhrzeit ist erforderlich."),
    personen: yup
        .number()
        .typeError("Anzahl der Personen ist erforderlich.")
        .integer("Bitte eine ganze Zahl.")
        .min(1, "Mindestens eine Person.")
        .max(9, "Ab 10 Personen bitte über Feiern anfragen.")
        .required("Anzahl der Personen ist erforderlich."),
    anlass: yup.string(),
    kommentar: yup.string(),
    website: yup.string().max(0, "Ungültige Anfrage"),
    formStartedAt: yup.number().required(),
    turnstileToken: yup
        .string()
        .test("turnstile-required", "Bitte bestätigen Sie, dass Sie kein Roboter sind.", turnstileRequired),
});

const ReservationForm = () => {
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const turnstileRef = useRef(null);
    const closure = useStatusStore((state) => state.closure);
    const {
        handleSubmit,
        register,
        setValue,
        watch,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: { zeit: "" },
    });

    useEffect(() => {
        setValue("formStartedAt", Date.now());
        setValue("website", "");
    }, [setValue]);

    const onSubmit = async ({ anlass, kommentar, ...data }) => {
        // Anlass hat kein eigenes Feld im Modell und geht in den Kommentar.
        const payload = {
            ...data,
            kommentar: [anlass && `Anlass: ${anlass}`, kommentar].filter(Boolean).join("\n"),
        };

        setLoading(true);
        setErrorMessage(null);
        try {
            await axios.post("/api/public/reservation", payload);
            setSent(true);
        } catch (error) {
            setErrorMessage(
                error?.response?.data?.message ||
                    "Fehler bei der Reservierung. Bitte versuchen Sie es später erneut.",
            );
            turnstileRef.current?.reset();
        } finally {
            setLoading(false);
        }
    };

    const datum = watch("datum");
    const zeit = watch("zeit");
    const times = getReservationTimes(datum);

    // Passt die gewählte Uhrzeit nach einem Datumswechsel nicht mehr, Auswahl zurücksetzen.
    useEffect(() => {
        if (zeit && !getReservationTimes(datum).includes(zeit)) setValue("zeit", "");
    }, [datum, zeit, setValue]);

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="hero-in grid min-w-0 grid-cols-1 desk:grid-cols-2 gap-5 bg-leinen p-6 desk:p-10"
            style={{ animationDelay: ".4s" }}
        >
            {sent ? (
                <SentMessage
                    title="Danke, Ihre Anfrage ist eingegangen."
                    text="Wir melden uns in Kürze zur Bestätigung."
                />
            ) : (
                <>
                    {closure && (
                        <div className="col-span-full border border-messing px-5 py-3.5 text-sm leading-normal">
                            {getClosureText(closure).period} In dieser Zeit sind keine Reservierungen möglich.
                        </div>
                    )}
                    <FormField
                        label="Datum"
                        type="date"
                        min={today()}
                        error={errors.datum?.message}
                        {...register("datum")}
                    />
                    <FormField label="Uhrzeit" as="select" error={errors.zeit?.message} {...register("zeit")}>
                        <option value="" disabled>
                            {!datum ? "Erst Datum wählen" : times.length > 0 ? "Bitte wählen" : "An diesem Tag geschlossen"}
                        </option>
                        {times.map((time) => (
                            <option key={time} value={time}>
                                {time} Uhr
                            </option>
                        ))}
                    </FormField>
                    <FormField
                        label="Personen"
                        type="number"
                        min={1}
                        max={9}
                        placeholder="2"
                        inputMode="numeric"
                        error={errors.personen?.message}
                        {...register("personen")}
                    />
                    <FormField label="Anlass" placeholder="optional" {...register("anlass")} />
                    <FormField
                        label="Vorname"
                        autoComplete="given-name"
                        error={errors.vorname?.message}
                        {...register("vorname")}
                    />
                    <FormField
                        label="Nachname"
                        autoComplete="family-name"
                        error={errors.nachname?.message}
                        {...register("nachname")}
                    />
                    <FormField
                        label="E-Mail"
                        type="email"
                        autoComplete="email"
                        error={errors.email?.message}
                        {...register("email")}
                    />
                    <FormField
                        label="Telefon"
                        type="tel"
                        autoComplete="tel"
                        error={errors.telefon?.message}
                        {...register("telefon")}
                    />
                    <FormField
                        label="Nachricht"
                        as="textarea"
                        rows={3}
                        placeholder="Kinderstuhl, Terrasse, Allergien …"
                        className="col-span-full"
                        {...register("kommentar")}
                    />
                    <SpamProtection
                        register={register}
                        setValue={setValue}
                        turnstileRef={turnstileRef}
                        error={errors.turnstileToken?.message}
                    />
                    <FormError>{errorMessage}</FormError>
                    <button type="submit" disabled={loading} className="btn btn-primary col-span-full mt-2 py-[18px]">
                        {loading ? "Wird gesendet …" : "Reservierung anfragen"}
                    </button>
                    <div className="col-span-full text-sm leading-[1.6] text-forest/70">
                        Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Angaben zur Bearbeitung der Anfrage zu.
                        Details in der{" "}
                        <a href="/datenschutz" className="underline">
                            Datenschutzerklärung
                        </a>
                        .
                    </div>
                </>
            )}
        </form>
    );
};

export default ReservationForm;
